class ImageCarousel extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.maxScale = 1;
    this.minScale = 0.5;
    this.slides = this.querySelectorAll(".carousel-slide");
    this.track = this.querySelector(".carousel-track");
    this.buttons = this.querySelectorAll("button");

    this.containerWidth = this.offsetWidth;
    this.slideSpacing = this.calculateSlideSpacing();
    this.mean = this.slides.length / 2;
    this.maxSpacing = this.slideSpacing;
    this.minSpacing = 10;
    this.slideWidth = parseInt(this.dataset.width);

    this.autoSlides = this.dataset.autorotate;
    this.speed = parseInt(this.dataset.speed);
    this.autoPlayInterval = null;
    this.isPaused = false;
    this.isAnimating = false;

    this.moveSlide = this.moveSlide.bind(this);
    this.startAutoPlay = this.startAutoPlay.bind(this);
    this.stopAutoPlay = this.stopAutoPlay.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.zDepthBase = this.slideWidth;
    this.init();
  }

  init() {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === this) {
          this.handleResize();
        }
      }
    });
    this.resizeObserver.observe(this);

    this.buttons.forEach((button) =>
      button.addEventListener("click", () => {
        const name = button.name;
        if (!this.isAnimating) this.moveSlide(name);
      })
    );

    this.addEventListener("mouseenter", this.stopAutoPlay);
    this.addEventListener("mouseleave", this.startAutoPlay);

    document.addEventListener("keydown", (e) => {
      if (!this.isAnimating) {
        if (e.key === "ArrowLeft") {
          this.moveSlide("prev");
        }
        if (e.key === "ArrowRight") {
          this.moveSlide("next");
        }
      }
    });

    // Add touch support
    let touchStartX = 0;
    let touchEndX = 0;

    this.addEventListener("touchstart", (e) => {
      touchStartX = e.changedTouches[0].screenX;
    });

    this.addEventListener("touchend", (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const diffX = touchStartX - touchEndX;

      if (Math.abs(diffX) > 50) {
        // Minimum swipe distance
        if (diffX > 0) {
          this.moveSlide("next");
        } else {
          this.moveSlide("prev");
        }
      }
    });

    this.updateSlides();
    if (this.autoSlides) {
      this.startAutoPlay();
    }
  }

  calculateSlideSpacing() {
    const minSpacing = 10;
    const maxSpacing = Math.min(this.containerWidth * 0.15, 100); // Cap maximum spacing
    return Math.max(minSpacing, maxSpacing);
  }

  calculateSlideWidth() {
    // Responsive width calculation
    const maxWidth = this.containerWidth * 0.8; // Maximum 80% of container
    const minWidth = Math.min(280, this.containerWidth * 0.6); // Minimum width
    return Math.min(maxWidth, Math.max(minWidth, this.baseSlideWidth));
  }

  updateDimensions() {
    this.containerWidth = this.offsetWidth;
    this.slideWidth = this.calculateSlideWidth();
    this.slideSpacing = this.calculateSlideSpacing();
    this.zDepthBase = this.slideWidth * 0.8; // Adjust z-depth based on slide width
  }

  handleResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      this.updateDimensions();
      this.updateSlides();
    }, 150);
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stopAutoPlay();
    } else if (this.autoSlides && !this.isPaused) {
      this.startAutoPlay();
    }
  }

  startAutoPlay() {
    if (this.autoSlides && !this.autoPlayInterval) {
      this.autoPlayInterval = setInterval(() => {
        this.moveSlide("next");
      }, this.speed);
    }
    this.isPaused = false;
  }

  stopAutoPlay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
    this.isPaused = true;
  }

  resetAutoPlay() {
    this.stopAutoPlay();
    this.startAutoPlay();
  }

  calculatePosition(index) {
    const totalSlides = this.slides.length;
    let distance = index - this.activeIndex;
    if (distance > totalSlides / 2) distance -= totalSlides;
    if (distance < -totalSlides / 2) distance += totalSlides;
    return distance;
  }
  
  calculateTransforms(distance) {
    const maxDistance = Math.floor(this.slides.length / 2);
    const absDistance = Math.abs(distance);
    const scaleRange = this.maxScale - this.minScale;
    let scale = this.maxScale - (scaleRange * absDistance) / maxDistance;
    const currentScale = Math.max(scale, this.minScale);

    const baseTranslate = (this.containerWidth / 2) * 0.35;
    const xTranslate = distance * baseTranslate * currentScale;
    const zDepth = -absDistance * this.zDepthBase;

    const opacity = Math.max(0.3, 1 - (absDistance / maxDistance) * 0.7);
    const shadowOpacity = Math.min(absDistance / maxDistance, 0.8);
    const rotationFactor = Math.min(5, this.containerWidth / 200);

    scale = this.containerWidth < 500 ? distance === 0 ? 1 : currentScale - 0.2 : distance === 0 ? 1.1 : currentScale;
    console.log("containerWidth", this.containerWidth)
    return {
      x: xTranslate,
      z: zDepth,
      scale: scale,
      opacity: opacity,
      zIndex: 100 - absDistance,
      shadowOpacity: shadowOpacity,
      rotate: distance * rotationFactor,
    };
  }

  updateSlides() {
    const tl = gsap.timeline({
      onStart: () => {
        this.isAnimating = true;
      },
      onComplete: () => {
        this.isAnimating = false;
      },
    });

    this.slides.forEach((slide, index) => {
      const distance = this.calculatePosition(index);
      const transforms = this.calculateTransforms(distance);

      slide.classList.toggle("active", distance === 0);
      let leftShadow = slide.querySelector(".slide-shadow-left");
      let rightShadow = slide.querySelector(".slide-shadow-right");

      if (!leftShadow) {
        leftShadow = document.createElement("div");
        leftShadow.className = "slide-shadow-left";
        slide.appendChild(leftShadow);
      }
      if (!rightShadow) {
        rightShadow = document.createElement("div");
        rightShadow.className = "slide-shadow-right";
        slide.appendChild(rightShadow);
      }

      tl.to(
        slide,
        {
          duration: 0.8,
          x: transforms.x,
          z: transforms.z,
          scale: transforms.scale,
          zIndex: transforms.zIndex,
          rotateY: transforms.rotate,
          ease: "power2.out",
          force3D: true,
        },
        0
      );

      tl.to(
        [leftShadow, rightShadow],
        {
          duration: 0.8,
          opacity: transforms.shadowOpacity,
          ease: "power2.out",
        },
        0
      );
    });
  }

  moveSlide(direction) {
    if (this.isAnimating) return;
    const totalSlides = this.slides.length;

    if (direction === "next") {
      this.activeIndex = (this.activeIndex + 1) % totalSlides;
    } else {
      this.activeIndex = (this.activeIndex - 1 + totalSlides) % totalSlides;
    }

    this.updateSlides();
    this.resetAutoPlay();
  }

  disconnectedCallback() {
    this.stopAutoPlay();
    this.resizeObserver.disconnect();
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
  }
}

customElements.define("image-carousel", ImageCarousel);
